import {
  ShipmentStopType,
  ShipmentType,
} from "@api/graphql/generated/generated-types";
import { defaultStopFormValues } from "@src/orders/create/utils/formInitialStates";

export interface StopOrderItem {
  id?: string;
  type: ShipmentStopType;
  isCompleted: boolean;
}

export interface StopOrderRowActions {
  canMoveUp: boolean;
  canMoveDown: boolean;
  // The last row has no Insert button: adding at the end is what Add Dropoff is for.
  isInsertBelowOffered: boolean;
  canInsertBelow: boolean;
  moveUpReason: string | null;
  moveDownReason: string | null;
  insertBelowReason: string | null;
}

const COMPLETED_REASON = "Completed stops cannot be moved.";

export function isMultiDropOffType(shipmentType: ShipmentType | undefined): boolean {
  return (
    shipmentType === ShipmentType.MultiDropOff ||
    shipmentType === ShipmentType.MultiDropOffRoundTrip
  );
}

/** Form stops carry no completion state, so it is looked up by stop id. */
export function toStopOrderItems(
  formStops: Array<{ id?: string; type: ShipmentStopType }>,
  completedStopIds: Set<string>,
): StopOrderItem[] {
  return formStops.map((stop) => ({
    id: stop.id,
    type: stop.type,
    isCompleted: !!stop.id && completedStopIds.has(stop.id),
  }));
}

function getLastCompletedIndex(stops: StopOrderItem[]): number {
  return stops.reduce(
    (lastIndex, item, itemIndex) => (item.isCompleted ? itemIndex : lastIndex),
    -1,
  );
}

export type StopMoveDirection = "up" | "down";

/**
 * The pending drop-off a stop trades places with. Completed stops are stepped over;
 * the pickup and the return stop end the search.
 */
export function getSwapIndex(
  stops: StopOrderItem[],
  index: number,
  direction: StopMoveDirection,
): number | null {
  const stop = stops[index];
  if (!stop || stop.isCompleted || stop.type !== ShipmentStopType.DropOff) {
    return null;
  }
  const step = direction === "up" ? -1 : 1;
  for (let candidate = index + step; candidate >= 0 && candidate < stops.length; candidate += step) {
    if (stops[candidate].isCompleted) continue;
    return stops[candidate].type === ShipmentStopType.DropOff ? candidate : null;
  }
  return null;
}

function getMoveUpReason(stops: StopOrderItem[], index: number): string | null {
  const stop = stops[index];
  if (stop.isCompleted) return COMPLETED_REASON;
  if (stop.type === ShipmentStopType.PickUp) return "The pickup stays first.";
  if (stop.type === ShipmentStopType.Return) return "The return stop stays last.";
  if (getSwapIndex(stops, index, "up") !== null) return null;
  return stops[index - 1]?.isCompleted
    ? "A stop cannot move above a completed stop."
    : "Stops stay below the pickup.";
}

function getMoveDownReason(stops: StopOrderItem[], index: number): string | null {
  const stop = stops[index];
  if (stop.isCompleted) return COMPLETED_REASON;
  if (stop.type === ShipmentStopType.PickUp) return "The pickup stays first.";
  if (stop.type === ShipmentStopType.Return) return "The return stop stays last.";
  if (getSwapIndex(stops, index, "down") !== null) return null;
  if (index === stops.length - 1) return "This is already the last stop.";
  return stops[index + 1]?.isCompleted
    ? "A stop cannot move below a completed stop."
    : "Stops stay above the return stop.";
}

function getInsertBelowReason(
  stops: StopOrderItem[],
  index: number,
  lastCompletedIndex: number,
): string | null {
  const stop = stops[index];
  if (stop.type === ShipmentStopType.Return) {
    return "A stop cannot be added after the return stop.";
  }
  if (index === stops.length - 1) {
    return "Use Add Dropoff to add a stop at the end.";
  }
  if (stop.isCompleted && index !== lastCompletedIndex) {
    return "A stop cannot be added between completed stops.";
  }
  if (!stop.isCompleted && index < lastCompletedIndex) {
    return "A stop cannot be added above a completed stop.";
  }
  return null;
}

/**
 * What the Move up / Move down / Insert stop below buttons may do on one row. The pickup
 * stays first, the return stays last, and completed stops keep their place.
 */
export function getStopOrderRowActions(
  stops: StopOrderItem[],
  index: number,
): StopOrderRowActions {
  if (!stops[index]) {
    return {
      canMoveUp: false,
      canMoveDown: false,
      isInsertBelowOffered: false,
      canInsertBelow: false,
      moveUpReason: null,
      moveDownReason: null,
      insertBelowReason: null,
    };
  }
  const lastCompletedIndex = getLastCompletedIndex(stops);
  const moveUpReason = getMoveUpReason(stops, index);
  const moveDownReason = getMoveDownReason(stops, index);
  const insertBelowReason = getInsertBelowReason(stops, index, lastCompletedIndex);
  return {
    canMoveUp: moveUpReason === null,
    canMoveDown: moveDownReason === null,
    isInsertBelowOffered: index < stops.length - 1,
    canInsertBelow: insertBelowReason === null,
    moveUpReason,
    moveDownReason,
    insertBelowReason,
  };
}

export function makeNewDropOff(stopDate?: Date) {
  return {
    ...defaultStopFormValues,
    timeWindow: { ...defaultStopFormValues.timeWindow },
    type: ShipmentStopType.DropOff,
    stopDate,
    deliverByDate: stopDate,
  };
}
